import { useCallback, useEffect, useRef } from 'react';

import { useDisplayNames } from '@spotify/confidence-core-react';
import {
  GetDisplayNamesQuery,
  isType,
  useGetDisplayNamesQuery,
} from '@spotify/confidence-graphql';

type FetchDisplayNamesArgs = {
  metrics?: string[];
  entities?: string[];
  assignmentTables?: string[];
  factTables?: string[];
  skip?: boolean;
};

function buildNameFilter(strings: string[]) {
  if (strings.length) {
    return `name:(${strings.map(s => `"${s}"`).join(' OR ')})`;
  }
  return '';
}

function buildFilterToMatchNothing() {
  return 'name:doesnotexist';
}

type FetchedDisplayNames = {
  metrics: Array<string>;
  entities: Array<string>;
  assignmentTables: Array<string>;
  factTables: Array<string>;
};

export function useFetchDisplayNames({
  metrics = [],
  entities = [],
  assignmentTables = [],
  factTables = [],
  skip,
}: FetchDisplayNamesArgs) {
  const { displayNames, setDisplayName } = useDisplayNames();

  const fetchedDisplayNames = useRef<FetchedDisplayNames>({
    metrics: [],
    entities: [],
    assignmentTables: [],
    factTables: [],
  });

  const updateDisplayNames = useCallback(
    (data: GetDisplayNamesQuery) => {
      const entries: [string, string][] = [];
      if (isType(data?.metrics, 'MetricsV1ListMetricsResponse')) {
        data.metrics.metrics.forEach(metric =>
          entries.push([metric.name, metric.displayName]),
        );
      }
      if (isType(data.entities, 'MetricsV1ListEntitiesResponse')) {
        data.entities.entities.forEach(entity =>
          entries.push([entity.name, entity.displayName]),
        );
      }
      if (
        isType(data.assignmentTables, 'MetricsV1ListAssignmentTablesResponse')
      ) {
        data.assignmentTables.assignmentTables.forEach(table =>
          entries.push([table.name, table.displayName]),
        );
      }
      if (isType(data.factTables, 'MetricsV1ListFactTablesResponse')) {
        data.factTables.factTables.forEach(table =>
          entries.push([table.name, table.displayName]),
        );
      }
      setDisplayName(Object.fromEntries(entries));
    },
    [setDisplayName],
  );
  const { refetch } = useGetDisplayNamesQuery({
    skip:
      skip ||
      (!metrics.length &&
        !entities.length &&
        !assignmentTables.length &&
        !factTables.length),
    variables: {
      metricsFilter: buildNameFilter(metrics),
      entitiesFilter: buildNameFilter(entities),
      assignmentTablesFilter: buildNameFilter(assignmentTables),
      factTablesFilter: buildNameFilter(factTables),
      pageSize: 50,
    },
    onCompleted: response => {
      updateDisplayNames(response);
    },
  });

  useEffect(() => {
    const missingMetrics = metrics?.filter(
      m =>
        m !== '' &&
        !displayNames.has(m) &&
        !fetchedDisplayNames.current.metrics.includes(m),
    );
    const missingEntities = entities?.filter(
      m =>
        m !== '' &&
        !displayNames.has(m) &&
        !fetchedDisplayNames.current.entities.includes(m),
    );
    const missingAssignmentTables = assignmentTables?.filter(
      m =>
        m !== '' &&
        !displayNames.has(m) &&
        !fetchedDisplayNames.current.assignmentTables.includes(m),
    );
    const missingFactTables = factTables?.filter(
      m =>
        m !== '' &&
        !displayNames.has(m) &&
        !fetchedDisplayNames.current.factTables.includes(m),
    );
    if (
      missingMetrics?.length ||
      missingEntities?.length ||
      missingAssignmentTables?.length ||
      missingFactTables?.length
    ) {
      fetchedDisplayNames.current.metrics.push(...missingMetrics);
      fetchedDisplayNames.current.entities.push(...missingEntities);
      fetchedDisplayNames.current.assignmentTables.push(
        ...missingAssignmentTables,
      );
      fetchedDisplayNames.current.factTables.push(...missingFactTables);

      refetch({
        metricsFilter: missingMetrics
          ? buildNameFilter(missingMetrics)
          : buildFilterToMatchNothing(),
        entitiesFilter: missingEntities
          ? buildNameFilter(missingEntities)
          : buildFilterToMatchNothing(),
        assignmentTablesFilter: missingAssignmentTables
          ? buildNameFilter(missingAssignmentTables)
          : buildFilterToMatchNothing(),
        factTablesFilter: missingFactTables
          ? buildNameFilter(missingFactTables)
          : buildFilterToMatchNothing(),
      }).then(r => updateDisplayNames(r.data));
    }
  }, [
    refetch,
    metrics,
    entities,
    assignmentTables,
    factTables,
    displayNames,
    updateDisplayNames,
  ]);
}
